/**
 * Idea dashboard — scan results + the 4 refinement queries.
 *
 * The refinement panel is FULLY DYNAMIC. Nothing about the four queries is
 * hardcoded: the server persists whatever the model generated, and the client
 * renders the control that each query's answerType asks for. Rendering is
 * defensive — an unknown type degrades to free text rather than showing nothing
 * (Journey spec §06).
 *
 * The panel renders before the comparison column on purpose: action before
 * analytics.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Logo, Mono, Wordmark } from '../components/ui';
import { ScanningMark } from '../components/ScanningMark';
import { Skeleton, TypeOut } from '../components/ScanSkeleton';

type AnswerType = 'text' | 'choice' | 'multi' | 'scale';

interface Query {
  id: string;
  ordinal: number;
  sourceTag: string;
  question: string;
  rationale: string | null;
  impact: string | null;
  answerType: string;
  options: string[] | null;
  placeholder: string | null;
  allowOther: boolean;
  answer: string | null;
  passed: boolean;
  resolved: boolean;
}

interface BriefRow {
  key: string;
  label: string;
  text: string;
  skipped: boolean;
}

interface ScanData {
  score: number;
  rank: number;
  cohortSize: number;
  percentile: string;
  dimensions: Record<string, number>;
  buckets: Record<string, number>;
  nearestIds: string[];
}

const PASS_LABEL = 'Not sure — let the research take it';

/**
 * Staged reveal after a fresh scan.
 *
 * The user lands here the instant the scan returns, and the screen fills in
 * rather than appearing complete: cohort bars build, the placement lands, then
 * the generated queries type in one at a time.
 *
 * This runs ONLY when arriving straight from a scan (`state.justScanned`).
 * Every later visit renders everything immediately — the reveal belongs to the
 * moment the idea was created, and replaying it on every visit would be an
 * obstacle rather than a delight.
 */
type Reveal = 'cohort' | 'placement' | 'queries' | 'done';

const REVEAL_AT: Record<Exclude<Reveal, 'cohort'>, number> = {
  placement: 900,
  queries: 1500,
  done: 1800,
};

/**
 * The dashboard's three phases, mapped from `idea.status`:
 *   refine   ← awaiting_refinement   the four questions are still open
 *   research ← in_research (+ draft) submitted, agents working
 *   report   ← report_ready          the verdict has landed
 */
type Phase = 'refine' | 'research' | 'report';

interface PhaseCopy {
  /** Header pill. */
  badge: string;
  /** Header sub-line next to the pill. */
  status: string;
  /** Mono eyebrow above the H1. Receives the cohort size. */
  eyebrow: (cohort: number) => string;
  title: string;
  sub: string;
  /** Chip on the read-only brief overlay. */
  briefLock: string;
  briefTone: 'amber' | 'green';
  /** Chip above the scan histogram. */
  baseChip: string;
  baseTone: 'amber' | 'green';
}

/**
 * Per-phase copy, transcribed from the prototype's `dashPhase` bindings.
 *
 * Kept as data rather than ternaries at each call site: the prototype changes
 * roughly a dozen strings between phases, and inlining them all made the
 * previous version unreadable enough that two of them were simply never
 * implemented.
 */
const PHASE_COPY: Record<Phase, PhaseCopy> = {
  refine: {
    badge: 'PRELIMINARY SCAN',
    status: 'RESEARCH STARTS AFTER STEP 3',
    eyebrow: (cohort) =>
      `STEP 3 OF 3 · SCANNED AGAINST ${cohort} IDEAS IN 40 SECONDS`,
    title: 'Here’s where your idea sits — and four questions that would move it.',
    sub: 'The scan pattern-matches your answers against our scored base in seconds. The 48-hour report is different — fresh research with cited sources, focused by how you answer these four questions.',
    briefLock: 'EDITABLE',
    briefTone: 'amber',
    baseChip: 'BASELINE — THE REPORT MAY MOVE THIS',
    baseTone: 'amber',
  },
  research: {
    badge: 'RESEARCH IN PROGRESS',
    status: 'REPORT WITHIN 48 HOURS',
    eyebrow: () => 'SUBMISSION COMPLETE · AGENTS ARE READING SOURCES NOW',
    title: 'Your idea is in research. Here’s where it started.',
    sub: 'The scan below is your baseline. When the report lands, you’ll see what the deep research moved — and in 90 days you can re-run it to track the deltas.',
    briefLock: 'LOCKED · IN RESEARCH',
    briefTone: 'green',
    baseChip: 'BASELINE — THE REPORT MAY MOVE THIS',
    baseTone: 'amber',
  },
  report: {
    badge: 'REPORT DELIVERED',
    status: 'DELIVERED',
    eyebrow: () => 'SCAN → REPORT · WHAT 48 HOURS OF RESEARCH CHANGED',
    title: 'The research is in. Here’s what moved.',
    sub: 'Both PDFs are in your inbox. Below, your baseline scan sits next to what the deep research changed — every claim is cited in the report.',
    briefLock: 'AS RESEARCHED',
    briefTone: 'green',
    baseChip: 'BASELINE — SUPERSEDED BY THE REPORT',
    baseTone: 'green',
  },
};

/** Verdict → banner copy and colour. */
const VERDICT_COPY: Record<string, { label: string; tone: 'green' | 'amber' }> = {
  proceed: { label: 'PROCEED', tone: 'green' },
  proceed_with_changes: { label: 'PROCEED WITH CHANGES', tone: 'amber' },
  rethink: { label: 'NEEDS A RETHINK', tone: 'amber' },
};

interface ReportData {
  verdict: string;
  headline: string;
  body: string;
  sourcesTotal: number;
  sourcesCited: number;
  score: number;
  rank: number;
  cohortSize: number;
  percentile: string;
  deliveredAt: number;
  moves: { label: string; from: number; to: number; delta: number; note: string }[];
  files: { id: string; kind: string }[];
}

export function IdeaDash() {
  const { ideaId } = useParams<{ ideaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const justScanned = Boolean(
    (location.state as { justScanned?: boolean } | null)?.justScanned,
  );

  /** How much of the screen has been revealed. Fresh scans start at the top. */
  const [reveal, setReveal] = useState<Reveal>(justScanned ? 'cohort' : 'done');
  /** Index of the last query whose text has finished typing. */
  const [typedUpTo, setTypedUpTo] = useState(justScanned ? -1 : Infinity);

  const [loading, setLoading] = useState(true);
  // Null until the fetch resolves. Previously this defaulted to
  // 'awaiting_refinement', which made the screen paint the refinement view for
  // every idea — including delivered ones — and then swap. The loading gate
  // below means no phase is ever guessed.
  const [status, setStatus] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanData | null>(null);
  const [report, setReport] = useState<ReportData | null>(null);
  /** When the idea was submitted to research — dates the timeline. */
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [queries, setQueries] = useState<Query[]>([]);
  const [open, setOpen] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [brief, setBrief] = useState<BriefRow[]>([]);
  const [briefOpen, setBriefOpen] = useState(false);
  /**
   * Whether the four refinement questions are expanded.
   *
   * Deliberately NOT persisted and NOT initialised from `phase` — the phase is
   * unknown until the fetch resolves, and seeding this from a guessed phase is
   * the same bug the `status` null-default fixed above. `queriesCollapsed`
   * below derives the default from the real phase; this holds only an explicit
   * user toggle. Collapsed is the right default on every visit, so a revisit
   * re-collapses rather than restoring the last expansion.
   */
  const [queriesOpen, setQueriesOpen] = useState(false);
  /** Local-only: the 90-day reminder has no backend yet. See ReRunCard. */
  const [reminded, setReminded] = useState(false);

  useEffect(() => {
    if (!ideaId) return;
    let cancelled = false;

    (async () => {
      const res = await fetch(`/api/scan/${ideaId}`);
      if (!res.ok) {
        if (!cancelled) navigate('/ideas', { replace: true });
        return;
      }
      const body = (await res.json()) as {
        status: string;
        scan: ScanData | null;
        queries: Query[];
        brief?: BriefRow[];
        report?: ReportData | null;
        submittedAt?: number;
      };
      if (cancelled) return;

      setStatus(body.status);
      setScan(body.scan);
      setReport(body.report ?? null);
      setSubmittedAt(body.submittedAt ?? null);
      setQueries(body.queries);
      setBrief(body.brief ?? []);
      // Resume at the first UNRESOLVED query, not at 0.
      const next = body.queries.findIndex((q) => !q.resolved);
      setOpen(next === -1 ? -1 : next);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [ideaId, navigate]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  /**
   * Advances the reveal once the data is actually in hand.
   *
   * Gated on `!loading` so the stages describe REAL content arriving, never a
   * timer running ahead of the fetch. If the request is slow the screen simply
   * holds on skeletons — it never claims a stage finished that hasn't.
   */
  useEffect(() => {
    if (!justScanned || loading) return;
    const timers = [
      setTimeout(() => setReveal('placement'), REVEAL_AT.placement),
      setTimeout(() => setReveal('queries'), REVEAL_AT.queries),
      setTimeout(() => setTypedUpTo(0), REVEAL_AT.done),
    ];
    return () => timers.forEach(clearTimeout);
  }, [justScanned, loading]);

  /**
   * Safety net for the reveal chain.
   *
   * The chain advances on each query finishing its type-out, so a scan that
   * returns ZERO queries — an AI degradation falls back to deterministic
   * output, and nothing guarantees four — would never fire the final
   * `setReveal('done')`, leaving the header spinning forever on a screen that
   * is actually complete. Anything that leaves the user watching a permanent
   * "working" state is worse than skipping the animation.
   */
  useEffect(() => {
    if (loading || queries.length > 0) return;
    setReveal('done');
    setTypedUpTo(Infinity);
  }, [loading, queries.length]);

  async function resolve(query: Query, passed: boolean) {
    const answer = passed ? '' : (drafts[query.id] ?? '').trim();
    if (!passed && !answer) return;

    const res = await fetch(`/api/scan/${ideaId}/refine`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ queryId: query.id, answer, passed }),
    });
    if (!res.ok) {
      setToast('Could not save that — try again.');
      return;
    }

    const next = queries.map((q) =>
      q.id === query.id ? { ...q, answer, passed, resolved: true } : q,
    );
    setQueries(next);

    const following = next.findIndex((q) => !q.resolved);
    setOpen(following);
    setToast(
      passed ? 'Passed to the agents — sharpness +15%' : 'Report sharpness +15%',
    );
  }

  async function complete() {
    const unresolved = queries.findIndex((q) => !q.resolved);
    if (unresolved !== -1) {
      // Clicking early opens the next outstanding query rather than failing
      // silently — the button stays clickable so it can explain itself.
      setOpen(unresolved);
      const left = queries.filter((q) => !q.resolved).length;
      setToast(`${left} question${left === 1 ? '' : 's'} left — answer it or pass it to research.`);
      return;
    }

    const res = await fetch(`/api/scan/${ideaId}/submit`, { method: 'POST' });
    const body = (await res.json()) as { referenceId?: string; status?: string };
    if (res.ok && body.referenceId) {
      setReference(body.referenceId);
      setStatus(body.status ?? 'in_research');
    }
  }

  async function passRest() {
    for (const q of queries.filter((x) => !x.resolved)) {
      await fetch(`/api/scan/${ideaId}/refine`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queryId: q.id, answer: '', passed: true }),
      });
    }
    setQueries(queries.map((q) => (q.resolved ? q : { ...q, passed: true, resolved: true })));
    setOpen(-1);
    setToast('Passed to the agents — sharpness 100%');
  }

  /** True while any stage of the fresh-scan reveal is still running. */
  const revealing = reveal !== 'done';

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--muted)',
          fontSize: 14.5,
        }}
      >
        Loading your scan…
      </div>
    );
  }

  const resolved = queries.filter((q) => q.resolved).length;
  const sharpness = Math.min(100, 40 + resolved * 15);

  /**
   * The screen has THREE phases, not two.
   *
   * This was a single `submitted` boolean, which collapsed in_research and
   * report_ready into one view: an idea whose report had landed looked
   * identical to one still being researched, and "Open report" from the ideas
   * list led to a research-in-progress screen. The prototype models this as
   * `dashPhase` with exactly these three values.
   *
   * `report` falls back to `research` when the outcome row is missing, so a
   * partially-written delivery shows a truthful older state rather than an
   * empty verdict banner.
   */
  const phase: Phase =
    status === 'report_ready' && report
      ? 'report'
      : status === 'awaiting_refinement'
        ? 'refine'
        : 'research';
  const copy = PHASE_COPY[phase];
  // Retained for the many call sites that only care "is the refinement panel
  // still open for input" — the semantics it always actually had.
  const submitted = phase !== 'refine';

  /**
   * Once submitted, the questions are settled history: locked, unanswerable,
   * and sitting above the analytics in a panel headed "before research
   * starts". The live thing on those screens is the timeline (research) or the
   * verdict (report), so the questions collapse to a summary row the user can
   * open to re-read. In `refine` they ARE the screen and never collapse.
   */
  const queriesCollapsed = submitted && !queriesOpen;
  const passed = queries.filter((q) => q.resolved && q.passed).length;
  const answered = resolved - passed;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(251,250,248,.86)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Logo />
          <Wordmark />
          <span
            style={{
              background: submitted ? 'var(--green-soft)' : 'var(--acc-soft)',
              border: `1px solid ${submitted ? 'var(--green-line)' : 'var(--acc-line)'}`,
              borderRadius: 20,
              padding: '3px 10px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              color: submitted ? 'var(--green)' : 'var(--acc)',
            }}
          >
            {copy.badge}
          </span>

          {/* Phase status line — "REPORT WITHIN 48 HOURS", "DELIVERED", etc.
              Hidden during the reveal, which owns this space. */}
          {!revealing && (
            <Mono size={10}>
              {phase === 'report' && report
                ? `${copy.status} ${formatDelivered(report.deliveredAt)}`
                : copy.status}
            </Mono>
          )}

          {/* Live stage read-out during the reveal. `role="status"` so a screen
              reader is told what is happening rather than watching silence. */}
          {revealing && (
            <span
              role="status"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: 'var(--ink2)',
              }}
            >
              <ScanningMark size={18} />
              {reveal === 'cohort'
                ? 'Comparing against the scored base…'
                : reveal === 'placement'
                  ? 'Placing your idea in the distribution…'
                  : 'Writing the questions that would move it…'}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={() => navigate('/ideas')}
            style={{
              background: 'none',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 13,
              color: 'var(--ink2)',
              cursor: 'pointer',
            }}
          >
            My ideas
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1080, margin: '0 auto', padding: '32px 20px 80px' }}>
        <Mono size={11}>{copy.eyebrow(scan?.cohortSize ?? 0)}</Mono>
        <h1
          style={{
            fontSize: 34,
            fontWeight: 600,
            lineHeight: 1.12,
            letterSpacing: '-.03em',
            margin: '12px 0 0',
            maxWidth: '24ch',
          }}
        >
          {copy.title}
        </h1>

        <p
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            color: 'var(--muted)',
            margin: '12px 0 0',
            maxWidth: '62ch',
          }}
        >
          {copy.sub}
        </p>

        {/* The verdict — report phase only, and first: it is the answer the
            whole funnel promised, so nothing should sit above it. */}
        {phase === 'report' && report && (
          <VerdictBanner report={report} onToast={setToast} />
        )}

        {/* What the 48 hours changed. Under the verdict, above the analytics:
            it is the evidence for the call that was just made. */}
        {phase === 'report' && report && <ResearchMoves moves={report.moves} />}

        <button
          onClick={() => setBriefOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginTop: 10,
            fontSize: 14,
            fontFamily: 'inherit',
            color: 'var(--acc-ink)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          View the brief the agents work from →
        </button>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
            gap: 24,
            marginTop: 28,
            alignItems: 'start',
          }}
        >
          {/* Refinement panel first: action before analytics. */}
          <section
            style={{
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(24,23,32,.05)',
            }}
          >
            {/* Research phase leads with the timeline: the questions below are
                answered and locked, so the live thing on this screen is what
                the agents are doing. */}
            {phase === 'research' && (
              <ResearchTimeline
                scan={scan}
                submittedAt={submittedAt}
                answered={resolved}
                total={queries.length}
              />
            )}

            <QueriesHeader
              queries={queries}
              resolved={resolved}
              answered={answered}
              passed={passed}
              submitted={submitted}
              collapsed={queriesCollapsed}
              onToggle={() => setQueriesOpen((v) => !v)}
            />

            {/* The collapse wraps ONLY the questions. The submission strip and
                GoFurther below stay visible: hiding those would bury the parts
                of these screens that are still live. */}
            <div id={QUERIES_PANEL_ID} hidden={queriesCollapsed}>
              {queries.map((q, i) => {
                // During the reveal each card waits its turn: the previous one
                // must finish typing before this one starts. `typedUpTo` is the
                // index currently allowed to type.
                if (reveal !== 'done' && reveal !== 'queries') {
                  return <QuerySkeleton key={q.id} index={i} />;
                }
                if (i > typedUpTo) {
                  return <QuerySkeleton key={q.id} index={i} />;
                }
                return (
                  <QueryCard
                    key={q.id}
                    query={q}
                    index={i}
                    open={open === i}
                    draft={drafts[q.id] ?? ''}
                    onDraft={(v) => setDrafts({ ...drafts, [q.id]: v })}
                    onToggle={() => setOpen(open === i ? -1 : i)}
                    onAnswer={() => resolve(q, false)}
                    onPass={() => resolve(q, true)}
                    locked={submitted}
                    // Only the card that just arrived types; earlier ones are
                    // already written, and a revisit types nothing at all.
                    typing={i === typedUpTo && typedUpTo !== Infinity}
                    onTyped={() => {
                      setTypedUpTo((n) => (n === Infinity ? n : n + 1));
                      if (i === queries.length - 1) setReveal('done');
                    }}
                  />
                );
              })}
            </div>

            <div style={{ padding: 18 }}>
              {submitted ? (
                <>
                  <div
                    style={{
                      background: 'var(--green-soft)',
                      border: '1px solid var(--green-line)',
                      borderRadius: 10,
                      padding: '12px 14px',
                    }}
                  >
                    {/* Once the report has landed, promising it "within 48h"
                        is stale and reads as though nothing happened. */}
                    <Mono size={10}>
                      {phase === 'report'
                        ? 'RESEARCH COMPLETE · THESE ANSWERS SHAPED THE REPORT'
                        : `SUBMISSION COMPLETE${reference ? ` · ${reference}` : ''} · REPORT WITHIN 48H`}
                    </Mono>
                  </div>

                  {/* The paid extras. Only ever AFTER submission — the landing
                      FAQ promises "nothing interrupts the flow to sell you
                      something; optional extras exist only after your report
                      lands", and this is the screen that has to keep that
                      promise. During research the block is informational; once
                      the report is in, it becomes actionable. */}
                  <GoFurther phase={phase} report={report} onToast={setToast} />
                </>
              ) : (
                <>
                  <Button
                    full
                    onClick={complete}
                    disabled={resolved < queries.length}
                    style={{ opacity: resolved < queries.length ? 0.5 : 1 }}
                  >
                    Complete my submission
                  </Button>
                  <button
                    onClick={passRest}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      marginTop: 12,
                      fontSize: 13.5,
                      color: 'var(--muted)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Pass the rest to research
                  </button>
                </>
              )}
            </div>
          </section>

          {/* Comparison column */}
          <section style={{ display: 'grid', gap: 16 }}>
            {/* Sharpness is its own card at the top of this column — it is the
                progress signal for the whole screen, not a panel sub-label. */}
            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--line)',
                borderRadius: 16,
                padding: 20,
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              >
                <Mono size={10}>REPORT SHARPNESS</Mono>
                <div style={{ flex: 1 }} />
                <Mono size={10}>
                  {resolved === queries.length
                    ? 'FULLY SHARPENED'
                    : `+15% PER ANSWER · ${queries.length - resolved} TO GO`}
                </Mono>
              </div>
              <div
                style={{
                  fontSize: 40,
                  fontWeight: 600,
                  letterSpacing: '-.035em',
                  lineHeight: 1,
                  margin: '10px 0 12px',
                }}
              >
                {sharpness}%
              </div>
              <div
                style={{
                  height: 7,
                  borderRadius: 4,
                  background: 'var(--gutter)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${sharpness}%`,
                    height: '100%',
                    background: 'var(--acc)',
                    transition: 'width .3s ease',
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: 'var(--muted)',
                  margin: '12px 0 0',
                }}
              >
                Founders who answer all four typically land{' '}
                <strong style={{ color: 'var(--ink2)' }}>
                  9 percentile spots higher
                </strong>{' '}
                than their scan. Passing counts too — it tells the agents exactly
                where to dig.
              </p>
            </div>

            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--line)',
                borderRadius: 16,
                padding: 20,
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              >
                <Mono size={10}>WHERE YOUR IDEA SITS</Mono>
                <div style={{ flex: 1 }} />
                {/* Says what this number IS relative to the report. Without it,
                    a delivered idea shows two different percentiles with no
                    indication which one is current. */}
                <span
                  style={{
                    background: `var(--${copy.baseTone}-soft)`,
                    border: `1px solid var(--${copy.baseTone}-line)`,
                    borderRadius: 20,
                    padding: '2px 8px',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    letterSpacing: '.05em',
                    color: `var(--${copy.baseTone})`,
                  }}
                >
                  {copy.baseChip}
                </span>
              </div>
              {/* Skeleton until the placement stage — a percentile is the one
                  number on this screen a founder will quote, so it must never
                  appear before it is the real one. */}
              {reveal === 'cohort' ? (
                <>
                  <Skeleton height={34} width="52%" style={{ margin: '8px 0 4px' }} />
                  <Skeleton height={13} width="68%" />
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 34,
                      fontWeight: 600,
                      letterSpacing: '-.03em',
                      margin: '8px 0 2px',
                      animation: justScanned ? 'ibRise .45s ease both' : undefined,
                    }}
                  >
                    {scan?.percentile ?? '—'}
                  </div>
                  <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>
                    rank {scan?.rank} of {scan?.cohortSize} scored ideas
                  </div>

                  {/* The report's placement, shown next to the baseline rather
                      than replacing it — the comparison IS the deliverable, so
                      overwriting the scan would erase what moved. */}
                  {phase === 'report' && report && (
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: '1px solid var(--line2)',
                      }}
                    >
                      <Mono size={10} color="var(--green)">
                        AFTER RESEARCH
                      </Mono>
                      <div
                        style={{
                          fontSize: 34,
                          fontWeight: 600,
                          letterSpacing: '-.03em',
                          margin: '6px 0 2px',
                          color: 'var(--green)',
                        }}
                      >
                        {report.percentile}
                      </div>
                      <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>
                        rank {report.rank} of {scan?.cohortSize} · score{' '}
                        {report.score.toFixed(1)} from {scan?.score.toFixed(1)}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Real buckets from the cohort — never a fabricated curve. */}
              <Histogram
                buckets={scan?.buckets ?? {}}
                score={scan?.score ?? 0}
                animate={justScanned}
                markYou={reveal !== 'cohort'}
              />
            </div>

            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--line)',
                borderRadius: 16,
                padding: 20,
              }}
            >
              <Mono size={10}>VS THE COHORT</Mono>
              <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                {Object.entries(scan?.dimensions ?? {}).map(([key, value]) => (
                  <div key={key}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 13,
                        color: 'var(--ink2)',
                      }}
                    >
                      <span>{humanise(key)}</span>
                      <span>{value.toFixed(1)}</span>
                    </div>
                    <div
                      style={{
                        height: 7,
                        borderRadius: 4,
                        background: 'var(--gutter)',
                        marginTop: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${(value / 10) * 100}%`,
                          height: '100%',
                          background: 'var(--acc)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* Re-run reminder — report phase only. Nothing to re-run until there
            is a report to compare against. */}
        {phase === 'report' && report && (
          <ReRunCard
            deliveredAt={report.deliveredAt}
            reminded={reminded}
            onRemind={() => {
              setReminded(true);
              setToast(`We'll email you on ${reRunDate(report.deliveredAt)}`);
            }}
          />
        )}

        {/* Where to go next. Every phase past the interview ends here rather
            than in a dead stop — the prototype closes each screen with these
            two, and without them a finished idea has no exit but the browser
            back button. */}
        {submitted && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 16,
              paddingTop: 20,
              borderTop: '1px solid var(--line2)',
            }}
          >
            <Button onClick={() => navigate('/capture')}>
              Evaluate another idea
            </Button>
            <Button variant="secondary" onClick={() => navigate('/ideas')}>
              All my ideas
            </Button>
            <div style={{ flex: 1 }} />
            <p
              style={{
                margin: 0,
                alignSelf: 'center',
                fontSize: 13.5,
                color: 'var(--muted)',
              }}
            >
              {phase === 'report'
                ? 'Both PDFs are in your inbox — nothing here expires.'
                : 'You can close this tab — everything arrives by email.'}
            </p>
          </div>
        )}
      </main>

      {briefOpen && (
        <BriefOverlay
          rows={brief}
          lockLabel={copy.briefLock}
          lockTone={copy.briefTone}
          onClose={() => setBriefOpen(false)}
        />
      )}

      {reference && (
        <QueueModal
          reference={reference}
          onClose={() => setReference(null)}
          onAllIdeas={() => navigate('/ideas')}
        />
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            borderRadius: 20,
            padding: '10px 18px',
            fontSize: 13.5,
            zIndex: 30,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function humanise(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function Histogram({
  buckets,
  score,
  animate = false,
  markYou = true,
}: {
  buckets: Record<string, number>;
  score: number;
  /** Bars grow in from zero, staggered left to right. Fresh scans only. */
  animate?: boolean;
  /** Highlight the user's band. Held back until the placement stage. */
  markYou?: boolean;
}) {
  const bands = Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b);
  if (bands.length === 0) return null;
  const max = Math.max(...Object.values(buckets));
  const youBand = Math.floor(score);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        height: 80,
        marginTop: 16,
      }}
    >
      {bands.map((band, i) => {
        const count = buckets[String(band)] ?? 0;
        const isYou = markYou && band === youBand;
        return (
          <div key={band} style={{ flex: 1, textAlign: 'center' }}>
            <div
              title={`${count} idea${count === 1 ? '' : 's'} scoring ${band}–${band + 1}`}
              style={{
                height: `${(count / max) * 56}px`,
                background: isYou ? 'var(--acc)' : 'var(--acc-line)',
                borderRadius: 3,
                minHeight: 3,
                // Bars grow left-to-right so the cohort visibly assembles
                // before the user's own band is called out.
                ...(animate
                  ? {
                      transformOrigin: 'bottom',
                      animation: `ibGrow .5s cubic-bezier(.2,.7,.3,1) ${i * 0.06}s both`,
                      transition: 'background .3s ease',
                    }
                  : { transition: 'background .3s ease' }),
              }}
            />
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                color: isYou ? 'var(--acc)' : 'var(--muted)',
                marginTop: 4,
                transition: 'color .3s ease',
              }}
            >
              {isYou ? `YOU ${score.toFixed(1)}` : band}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface CardProps {
  query: Query;
  index: number;
  open: boolean;
  draft: string;
  locked: boolean;
  onDraft: (v: string) => void;
  onToggle: () => void;
  onAnswer: () => void;
  onPass: () => void;
  /** Type the question in rather than rendering it whole. Fresh scans only. */
  typing?: boolean;
  /** Fired once the question text has finished typing. */
  onTyped?: () => void;
}

/** DOM id linking the toggle to the panel it controls, for `aria-controls`. */
const QUERIES_PANEL_ID = 'refinement-queries';

/**
 * "four questions" / "three questions" — the scan generates whatever the model
 * returned, and a degraded scan can return fewer. The header used to hardcode
 * "Four", which would misdescribe those.
 */
const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six'];

function countWord(n: number) {
  const word = NUMBER_WORDS[n] ?? String(n);
  return `${word} question${n === 1 ? '' : 's'}`;
}

function initialCap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Header for the refinement panel — and, once submitted, its collapse toggle.
 *
 * In `refine` the questions ARE the screen, so this is a plain heading: a
 * section that cannot collapse must not look clickable. After submission the
 * questions are locked history sitting above the analytics, so the header
 * becomes a button and the copy switches to past tense — "before research
 * starts" next to "RESEARCH COMPLETE" reads as a bug.
 */
function QueriesHeader({
  queries,
  resolved,
  answered,
  passed,
  submitted,
  collapsed,
  onToggle,
}: {
  queries: Query[];
  resolved: number;
  answered: number;
  passed: number;
  submitted: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const style: CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'var(--acc-soft)',
    border: 'none',
    borderBottom: '1px solid var(--acc-line)',
    padding: '14px 18px',
    font: 'inherit',
    color: 'inherit',
  };

  const inner = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Mono size={10}>
          {submitted
            ? 'GENERATED FROM YOUR ANSWERS · ANSWERED BEFORE RESEARCH'
            : 'SHARPEN THE RESEARCH · GENERATED FROM YOUR ANSWERS'}
        </Mono>
        <div style={{ flex: 1 }} />
        <Mono size={10}>
          {resolved} OF {queries.length}
        </Mono>
        {submitted && (
          <Mono size={10} color="var(--acc)">
            {collapsed ? 'SHOW' : 'HIDE'}
          </Mono>
        )}
      </div>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '-.02em',
          margin: '10px 0 0',
        }}
      >
        {submitted
          ? `The ${countWord(queries.length)} that focused the research`
          : `${initialCap(countWord(queries.length))} before research starts`}
      </h2>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.6,
          color: 'var(--muted)',
          margin: '6px 0 12px',
        }}
      >
        {/* Collapsed, the answered/passed split is the one thing that would
            otherwise become invisible: `resolved` counts both the same way, and
            passing is a real choice the agents act on. */}
        {submitted
          ? `${answered} answered · ${passed} passed to research.`
          : 'Each answer sharpens the report. Passing is a real answer too — the agents research it instead.'}
      </p>
      <div style={{ display: 'flex', gap: 6 }}>
        {queries.map((q) => (
          <div
            key={q.id}
            style={{
              height: 4,
              flex: 1,
              borderRadius: 3,
              background: q.resolved ? 'var(--acc)' : 'var(--line)',
            }}
          />
        ))}
      </div>
    </>
  );

  if (!submitted) return <div style={style}>{inner}</div>;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-controls={QUERIES_PANEL_ID}
      style={{ ...style, cursor: 'pointer' }}
    >
      {inner}
    </button>
  );
}

/**
 * Placeholder for a query that has not been revealed yet.
 *
 * Matches QueryCard's geometry — same padding, same 22px badge, same two text
 * lines — so the real card replaces it without the list shifting.
 */
function QuerySkeleton({ index }: { index: number }) {
  return (
    <div style={{ borderBottom: '1px solid var(--line2)' }}>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          padding: '16px 18px',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            flexShrink: 0,
            background: 'var(--gutter)',
            border: '1px solid var(--line)',
          }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <Skeleton height={9} width="42%" />
          <Skeleton
            height={15}
            width={`${92 - index * 6}%`}
            style={{ marginTop: 7 }}
          />
        </span>
      </div>
    </div>
  );
}

function QueryCard({
  query,
  index,
  open,
  draft,
  locked,
  onDraft,
  onToggle,
  onAnswer,
  onPass,
  typing = false,
  onTyped,
}: CardProps) {
  // Unknown types degrade to free text rather than rendering nothing.
  const type: AnswerType = (
    ['text', 'choice', 'multi', 'scale'] as const
  ).includes(query.answerType as AnswerType)
    ? (query.answerType as AnswerType)
    : 'text';

  return (
    <div style={{ borderBottom: '1px solid var(--line2)' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          padding: '16px 18px',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            background: query.resolved ? 'var(--acc)' : 'var(--acc-soft)',
            color: query.resolved ? '#fff' : 'var(--acc)',
            border: '1px solid var(--acc-line)',
          }}
        >
          {query.resolved ? '✓' : index + 1}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontFamily: 'var(--font-mono)',
              fontSize: 9.5,
              letterSpacing: '.06em',
              color: 'var(--amber)',
            }}
          >
            {query.sourceTag}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 17,
              fontWeight: 500,
              lineHeight: 1.4,
              marginTop: 4,
              color: 'var(--ink)',
            }}
          >
            {typing ? (
              <TypeOut text={query.question} onDone={onTyped} />
            ) : (
              query.question
            )}
          </span>
          {query.resolved && (
            <span
              style={{
                display: 'block',
                fontSize: 13,
                color: 'var(--muted)',
                marginTop: 4,
              }}
            >
              {query.passed ? 'Passed to research' : query.answer}
            </span>
          )}
        </span>
      </button>

      {open && !locked && (
        <div style={{ padding: '0 18px 18px 52px' }}>
          {/* Missing rationale hides the element rather than showing an empty
              byline. */}
          {query.rationale && (
            <>
              <Mono size={9.5}>IDEABRIEF AI · WHY THIS QUESTION</Mono>
              <p
                style={{
                  fontSize: 14,
                  color: 'var(--ink2)',
                  lineHeight: 1.6,
                  margin: '6px 0 12px',
                }}
              >
                {query.rationale}
              </p>
            </>
          )}

          {query.impact && (
            <span
              style={{
                display: 'inline-block',
                background: 'var(--acc-soft)',
                border: '1px solid var(--acc-line)',
                borderRadius: 20,
                padding: '3px 9px',
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                letterSpacing: '.06em',
                color: 'var(--acc)',
                marginBottom: 12,
              }}
            >
              {query.impact}
            </span>
          )}

          <AnswerControl
            type={type}
            query={query}
            draft={draft}
            onDraft={onDraft}
          />

          <div
            style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}
          >
            <Button size="sm" onClick={onAnswer} disabled={!draft.trim()}>
              Answer
            </Button>
            {/* Available on EVERY type, without exception. */}
            <button
              onClick={onPass}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                color: 'var(--muted)',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {PASS_LABEL}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AnswerControl({
  type,
  query,
  draft,
  onDraft,
}: {
  type: AnswerType;
  query: Query;
  draft: string;
  onDraft: (v: string) => void;
}) {
  // A choice/multi with no usable options degrades to text.
  const options = query.options?.length ? query.options.slice(0, 4) : null;

  if (type === 'choice' && options) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o) => (
          <button
            key={o}
            onClick={() => onDraft(o)}
            style={pill(draft === o)}
          >
            {o}
          </button>
        ))}
      </div>
    );
  }

  if (type === 'multi' && options) {
    const selected = draft ? draft.split(' · ') : [];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              onClick={() =>
                onDraft(
                  (on ? selected.filter((s) => s !== o) : [...selected, o]).join(
                    ' · ',
                  ),
                )
              }
              style={pill(on)}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }

  if (type === 'scale') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onDraft(String(n))}
            style={{ ...pill(draft === String(n)), minWidth: 44, minHeight: 44 }}
          >
            {n}
          </button>
        ))}
      </div>
    );
  }

  return (
    <textarea
      value={draft}
      onChange={(e) => onDraft(e.target.value)}
      placeholder={query.placeholder ?? ''}
      rows={3}
      style={{
        width: '100%',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 10,
        fontSize: 15,
        fontFamily: 'inherit',
        lineHeight: 1.55,
        resize: 'vertical',
        outline: 'none',
        color: 'var(--ink)',
      }}
    />
  );
}

const pill = (active: boolean) =>
  ({
    background: active ? 'var(--acc-soft)' : 'var(--card)',
    border: `1px solid ${active ? 'var(--acc)' : 'var(--line)'}`,
    borderRadius: 20,
    padding: '9px 14px',
    fontSize: 13.5,
    color: active ? 'var(--acc)' : 'var(--ink2)',
    cursor: 'pointer',
    minHeight: 44,
  }) as const;

/**
 * Read-only view of the brief the agents work from.
 *
 * Read-only on purpose: once the scan has run, the brief is what the score was
 * computed against, so letting it be edited here would make the displayed
 * percentile refer to text that no longer exists.
 */
function BriefOverlay({
  rows,
  lockLabel,
  lockTone,
  onClose,
}: {
  rows: BriefRow[];
  /** Phase-dependent: EDITABLE / LOCKED · IN RESEARCH / AS RESEARCHED. */
  lockLabel: string;
  lockTone: 'amber' | 'green';
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(24,23,32,.4)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 40,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)',
          borderRadius: 16,
          maxWidth: 640,
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '18px 24px',
            borderBottom: '1px solid var(--line2)',
            position: 'sticky',
            top: 0,
            background: 'var(--card)',
          }}
        >
          <Mono size={10}>THE BRIEF</Mono>
          <span
            style={{
              background: `var(--${lockTone}-soft)`,
              border: `1px solid var(--${lockTone}-line)`,
              borderRadius: 20,
              padding: '2px 8px',
              fontFamily: 'var(--font-mono)',
              fontSize: 9.5,
              letterSpacing: '.06em',
              color: `var(--${lockTone})`,
            }}
          >
            {lockLabel}
          </span>
          <div style={{ flex: 1 }} />
          <Button size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>

        {rows.map((row) => (
          <div
            key={row.key}
            style={{ padding: '14px 24px', borderBottom: '1px solid var(--line2)' }}
          >
            <Mono size={10}>{row.label}</Mono>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 14.5,
                lineHeight: 1.6,
                color: row.skipped ? 'var(--muted)' : 'var(--ink2)',
              }}
            >
              {row.skipped
                ? "Not answered — we'll research this ourselves and flag what we assume."
                : row.text || '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Confirmation is a modal over the dashboard, never its own route. */
function QueueModal({
  reference,
  onClose,
  onAllIdeas,
}: {
  reference: string;
  onClose: () => void;
  onAllIdeas: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(24,23,32,.4)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 40,
        padding: 20,
      }}
    >
      <div
        style={{
          background: 'var(--card)',
          borderRadius: 16,
          padding: 28,
          maxWidth: 480,
          width: '100%',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'var(--green-soft)',
            border: '1px solid var(--green-line)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--green)',
            fontSize: 20,
          }}
        >
          ✓
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 600, margin: '16px 0 8px' }}>
          Your idea is in the queue.
        </h2>
        <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>
          We&apos;ll email you within 48 hours — an executive deck, the full
          analysis, and a verdict page.
        </p>

        <div
          style={{
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: '12px 14px',
            margin: '18px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <Mono size={9.5}>REFERENCE ID</Mono>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, marginTop: 2 }}>
              {reference}
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              navigator.clipboard?.writeText(reference);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy ID'}
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button onClick={onClose}>Back to this idea</Button>
          <Button variant="secondary" onClick={onAllIdeas}>
            All my ideas
          </Button>
        </div>
      </div>
    </div>
  );
}

/** "DELIVERED 3 AUG · 09:12 GMT" from an epoch-ms timestamp. */
function formatDelivered(ms: number): string {
  const d = new Date(ms);
  const date = d
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .toUpperCase();
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${date} · ${time} GMT`;
}

/**
 * The verdict — the report phase's headline block.
 *
 * Sits directly under the H1 and above the analytics, because it is the answer
 * the whole funnel promised. Everything below it is supporting evidence.
 *
 * The PDFs are NOT linked directly: each button hits /api/reports/:id, which
 * re-checks ownership and email verification server-side, then streams the R2
 * object. No object key or storage URL is ever exposed to the client.
 */
function VerdictBanner({
  report,
  onToast,
}: {
  report: ReportData;
  onToast: (msg: string) => void;
}) {
  const v = VERDICT_COPY[report.verdict] ?? { label: 'VERDICT', tone: 'green' as const };

  return (
    <section
      style={{
        marginTop: 24,
        background: `var(--${v.tone}-soft)`,
        border: `1px solid var(--${v.tone}-line)`,
        borderRadius: 16,
        padding: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            background: `var(--${v.tone})`,
            color: '#fff',
            borderRadius: 20,
            padding: '4px 11px',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '.06em',
            fontWeight: 500,
          }}
        >
          VERDICT · {v.label}
        </span>
        <Mono size={10}>
          BUILT FROM {report.sourcesTotal} SOURCES · {report.sourcesCited} CITED
        </Mono>
      </div>

      <h2
        style={{
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '-.02em',
          lineHeight: 1.3,
          margin: '14px 0 0',
          maxWidth: '46ch',
        }}
      >
        {report.headline}
      </h2>

      <p
        style={{
          fontSize: 15,
          lineHeight: 1.65,
          color: 'var(--ink2)',
          margin: '10px 0 0',
          maxWidth: '62ch',
        }}
      >
        {report.body}
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
        <Button
          size="sm"
          onClick={() => {
            // Clipboard is unavailable over plain HTTP and can be blocked by
            // permissions policy, so the toast reports what actually happened
            // rather than claiming success unconditionally.
            navigator.clipboard
              ?.writeText(window.location.href)
              .then(() => onToast(`Verdict link copied — ${report.percentile} of the base`))
              .catch(() => onToast('Could not copy the link — copy it from the address bar'));
          }}
        >
          Share your {report.percentile} verdict
        </Button>

        {report.files.map((f) => (
          <Button
            key={f.id}
            size="sm"
            variant="secondary"
            onClick={() => {
              window.location.href = `/api/reports/${f.id}`;
            }}
          >
            {f.kind === 'deck'
              ? 'Executive deck (PDF)'
              : f.kind === 'detailed'
                ? 'Detailed report (PDF)'
                : 'Verdict page'}
          </Button>
        ))}
      </div>
    </section>
  );
}

/**
 * "WHAT THE RESEARCH MOVED · SCAN → REPORT".
 *
 * Each row pairs a dimension's before/after with the sentence explaining it.
 * The note is the point: a number that moved without a reason reads as noise,
 * and the product's claim is that the movement is researched.
 *
 * Direction is coloured, not signed-by-convention: a FALL in competition
 * intensity is good news, so the caller decides nothing and this simply shows
 * up green for increases and amber for decreases, with the note carrying the
 * interpretation.
 */
function ResearchMoves({ moves }: { moves: ReportData['moves'] }) {
  if (moves.length === 0) return null;

  return (
    <section
      style={{
        marginTop: 24,
        background: 'var(--card)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(24,23,32,.05)',
      }}
    >
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--line2)',
          background: 'var(--gutter)',
        }}
      >
        <Mono size={10}>WHAT THE RESEARCH MOVED · SCAN → REPORT</Mono>
      </div>

      {moves.map((m) => {
        const up = m.delta >= 0;
        const tone = up ? 'green' : 'amber';
        return (
          <div
            key={m.label}
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--line2)',
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 14.5, color: 'var(--ink)', flex: '1 1 190px' }}>
              {m.label}
            </span>

            <Mono size={11}>
              {m.from.toFixed(1)} → {m.to.toFixed(1)}
            </Mono>

            <span
              style={{
                background: `var(--${tone}-soft)`,
                border: `1px solid var(--${tone}-line)`,
                borderRadius: 20,
                padding: '2px 9px',
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '.04em',
                color: `var(--${tone})`,
                flex: 'none',
              }}
            >
              {/* U+2212 MINUS, not a hyphen: it aligns with digits. */}
              {up ? '+' : '−'}
              {Math.abs(m.delta).toFixed(1)}
            </span>

            <span
              style={{
                fontSize: 13.5,
                color: 'var(--muted)',
                lineHeight: 1.5,
                flex: '1 1 100%',
              }}
            >
              {m.note}
            </span>
          </div>
        );
      })}
    </section>
  );
}

/** "14:32 GMT" from epoch ms. */
function clockUTC(ms: number): string {
  return `${new Date(ms).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })} GMT`;
}

/** "3 Aug, 14:32 GMT" from epoch ms. */
function stampUTC(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })}, ${clockUTC(ms)}`;
}

/**
 * The research-phase timeline.
 *
 * Four stages, three states: done (solid green dot), running (pulsing accent
 * dot), pending (hollow ring). The connector below each stage is coloured by
 * that stage's state, so the line itself reads as progress.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: the prototype's running stage carries a
 * live counter ("212 SOURCES READ · 14 COMPETITORS EXAMINED"). There is no
 * research pipeline yet and therefore no such number — inventing one would put
 * a fabricated statistic in front of a user who is being asked to trust the
 * report. The stage instead names what is being worked on, which is true.
 * Wire the counter up when the pipeline can report it.
 */
function ResearchTimeline({
  scan,
  submittedAt,
  answered,
  total,
}: {
  scan: ScanData | null;
  submittedAt: number | null;
  answered: number;
  total: number;
}) {
  // 48h is the promise the product makes everywhere else; deriving the ETA
  // from the submission keeps this screen agreeing with the emails.
  const eta = submittedAt ? submittedAt + 48 * 60 * 60 * 1000 : null;

  const stages: {
    title: string;
    detail: string;
    state: 'done' | 'running' | 'pending';
  }[] = [
    {
      title: 'Brief locked',
      detail: `Interview + your ${total} refinement${total === 1 ? '' : 's'}${
        submittedAt ? ` · ${clockUTC(submittedAt)}` : ''
      }`,
      state: 'done',
    },
    {
      title: 'Baseline scan',
      detail: scan
        ? `${scan.score.toFixed(1)} · ${scan.percentile} of ${scan.cohortSize} — your before picture`
        : 'Your before picture',
      state: 'done',
    },
    {
      title: 'Deep research — running now',
      detail: 'Market sizing, competitor teardown, timing, risks',
      state: 'running',
    },
    {
      title: 'Report drafted & emailed',
      detail: `Two PDFs + verdict page${eta ? ` · by ${stampUTC(eta)} at the latest` : ''}`,
      state: 'pending',
    },
  ];

  return (
    <>
      <div
        style={{
          padding: '22px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--green-soft)',
        }}
      >
        <Mono size={10.5} color="var(--green)">
          RESEARCH IN PROGRESS
          {submittedAt ? ` · STARTED ${stampUTC(submittedAt).toUpperCase()}` : ''}
        </Mono>
        <h2
          style={{
            margin: '12px 0 0',
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: '-.02em',
            lineHeight: 1.3,
          }}
        >
          Agents are on it. Nothing more needed from you.
        </h2>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13.5,
            lineHeight: 1.55,
            color: 'var(--ink2)',
          }}
        >
          We&apos;ll email only if something needs your input — otherwise the next
          thing you see is the report. {answered} of {total} refinement
          {total === 1 ? '' : 's'} went with it.
        </p>
      </div>

      <ol
        style={{
          padding: '22px 24px',
          margin: 0,
          listStyle: 'none',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        {stages.map((s, i) => {
          const last = i === stages.length - 1;
          const dot =
            s.state === 'pending'
              ? { background: '#fff', border: '2px solid var(--line)' }
              : s.state === 'running'
                ? { background: 'var(--acc)', animation: 'ibPulse 1.8s ease-out infinite' }
                : { background: 'var(--green)' };

          return (
            <li key={s.title} style={{ display: 'flex', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 'none',
                  width: 18,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    flex: 'none',
                    ...dot,
                  }}
                />
                {!last && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 2,
                      flex: 1,
                      minHeight: 26,
                      background:
                        s.state === 'done' ? 'var(--green-line)' : 'var(--line)',
                    }}
                  />
                )}
              </div>

              <div style={{ paddingBottom: last ? 0 : 18 }}>
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    letterSpacing: '-.01em',
                    color:
                      s.state === 'running'
                        ? 'var(--acc-ink)'
                        : s.state === 'pending'
                          ? 'var(--ink2)'
                          : 'var(--ink)',
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}
                >
                  {s.detail}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}

/** "30 October 2026" — 90 days after delivery. */
function reRunDate(deliveredAt: number): string {
  return new Date(deliveredAt + 90 * 24 * 60 * 60 * 1000).toLocaleDateString(
    'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' },
  );
}

/**
 * The optional paid extras.
 *
 * PLACEMENT IS THE PRODUCT PROMISE. The landing FAQ says "nothing interrupts
 * the flow to sell you something; optional extras exist only after your report
 * lands". So this renders only once the idea is submitted, never during the
 * interview or the refinement questions, and the caller enforces that.
 *
 * Two states:
 *   research — informational. The report is not here yet, so there is nothing
 *              to act on; the block sets expectations and no button is live.
 *   report   — actionable, and the intro offer is gated on placement. The
 *              prototype's copy ("top-decile ideas can opt in") is a promise
 *              about who qualifies, so a 71st-percentile idea must not be shown
 *              an "intro eligible" banner it does not qualify for.
 *
 * NEITHER BUTTON TRANSACTS. There is no payments integration and no investor
 * pipeline; both record intent and say so plainly. A button that implied a
 * completed booking or a sent introduction would be a lie about a paid service.
 */
function GoFurther({
  phase,
  report,
  onToast,
}: {
  phase: Phase;
  report: ReportData | null;
  onToast: (msg: string) => void;
}) {
  // Top decile, computed from rank and cohort size rather than parsed out of
  // the "91ST PERCENTILE" string — that label is formatted for humans and
  // rounds, so reading logic back out of it would drift from the real
  // placement. (It also survived the TOP-n%-to-percentile switch untouched,
  // which parsing the string would not have.)
  const eligible =
    phase === 'report' &&
    report !== null &&
    report.cohortSize > 0 &&
    report.rank / report.cohortSize <= 0.1;

  return (
    <div
      style={{
        marginTop: 14,
        border: '1px solid var(--acc-line)',
        background: 'var(--acc-soft)',
        borderRadius: 10,
        padding: '14px 16px',
      }}
    >
      <Mono size={10} color="var(--acc-ink)">
        {phase === 'report'
          ? eligible
            ? `GO FURTHER · ${report?.percentile} — INTRO ELIGIBLE`
            : 'GO FURTHER — OPTIONAL'
          : 'IF YOU WANT TO GO FURTHER — AFTER YOUR REPORT LANDS'}
      </Mono>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '10px 0 0',
          display: 'grid',
          gap: 8,
        }}
      >
        <li style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)' }}>
          <strong style={{ fontWeight: 600 }}>Founder deep-dive · £199</strong> —
          interview scripts for your segment, a defensible TAM model, and a pitch
          review against your report.
        </li>
        <li style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)' }}>
          <strong style={{ fontWeight: 600 }}>Investor introductions · free</strong>{' '}
          — top-decile ideas can opt in to warm intros. Investors pay us for the
          deal flow, never you.
        </li>
      </ul>

      {phase === 'report' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {eligible && (
            <Button
              size="sm"
              onClick={() =>
                onToast("Noted — we'll email you before approaching any investor")
              }
            >
              Request investor intros
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              onToast("Noted — we'll email you to arrange the deep-dive")
            }
          >
            Book the deep-dive · £199
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The 90-day re-run reminder.
 *
 * LOCAL-ONLY TODAY. There is no reminders table and no scheduled job, so the
 * button records nothing server-side and the state dies with the page. The
 * copy therefore says what will actually happen rather than "reminder set" —
 * promising a mail we cannot send is the same class of bug as the sign-up
 * masking that told users to check an inbox no email ever reached.
 */
function ReRunCard({
  deliveredAt,
  reminded,
  onRemind,
}: {
  deliveredAt: number;
  reminded: boolean;
  onRemind: () => void;
}) {
  return (
    <div
      style={{
        marginTop: 16,
        border: '1px solid var(--line)',
        borderRadius: 16,
        background: 'var(--card)',
        padding: '20px 22px',
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
        boxShadow: '0 1px 2px rgba(24,23,32,.05)',
      }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.015em' }}>
          See what moves in 90 days
        </div>
        <p
          style={{
            margin: '5px 0 0',
            fontSize: 13.5,
            lineHeight: 1.55,
            color: 'var(--muted)',
          }}
        >
          Re-run the evaluation on {reRunDate(deliveredAt)} and we&apos;ll show
          the deltas — timing, competition, your percentile.
        </p>
      </div>
      <Button
        size="sm"
        variant={reminded ? 'secondary' : 'primary'}
        onClick={onRemind}
        disabled={reminded}
      >
        {reminded ? '✓ Reminder requested' : 'Remind me in 90 days'}
      </Button>
    </div>
  );
}
